import { Crown, MoreVertical, RotateCcw, WifiOff } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { CatFace } from "@/components/yarn/Cat";

function Status({ room, p }) {
  if (room.phase === "writing") return <span className={p.submitted ? "text-[var(--yarn-sage)]" : "text-[var(--yarn-muted)]"}>{p.submitted ? "Done" : "Writing..."}</span>;
  if (room.phase === "voting") return <span className={p.voted ? "text-[var(--yarn-sage)]" : "text-[var(--yarn-muted)]"}>{p.voted ? "Voted" : "Voting..."}</span>;
  return null;
}

function HostMenu({ p, send }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="h-9 w-9 grid place-items-center rounded-full hover:bg-[var(--yarn-linen)] transition-colors" aria-label={`Manage ${p.username}`} data-testid={`player-menu-${p.username}`}>
          <MoreVertical size={16} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="rounded-xl border-2 font-display">
        <DropdownMenuItem onClick={() => send("make_host", { user_id: p.id })} data-testid={`make-host-${p.username}`}>Make host</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => send("kick", { user_id: p.id })} data-testid={`kick-${p.username}`}>Kick (can rejoin)</DropdownMenuItem>
        <DropdownMenuItem className="text-red-700 focus:text-red-700" onClick={() => send("ban", { user_id: p.id })} data-testid={`ban-${p.username}`}>Ban from this game</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function PlayerList({ room, me, isHost, send, moods = {} }) {
  return (
    <div className="patch p-5" data-testid="player-list">
      <p className="font-display text-sm uppercase tracking-widest text-[var(--yarn-muted)] mb-3">
        The circle · {room.players.length}/{room.max_players}
      </p>
      <ul className="space-y-1">
        {room.players.map((p) => (
          <li key={p.id} className={`flex items-center gap-3 py-2 ${p.connected ? "" : "opacity-50"}`} data-testid={`player-row-${p.username}`}>
            <span key={moods[p.id] || "calm"} className={moods[p.id] ? "cat-wiggle inline-block" : "inline-block"}>
              <CatFace id={p.id} look={p.cat} mood={moods[p.id]} size={38} withYarn testid={`player-cat-${p.username}`} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-display font-medium truncate flex items-center gap-1.5">
                {p.username}
                {p.id === me && <span className="text-xs text-[var(--yarn-muted)]">(you)</span>}
                {p.is_host && <Crown size={14} className="text-[var(--yarn-mustard)]" data-testid={`host-crown-${p.username}`} />}
                {p.restart && <RotateCcw size={13} className="text-[var(--yarn-plum)]" />}
                {!p.connected && <WifiOff size={13} />}
              </p>
              <p className="text-xs flex gap-2">
                <span className="text-[var(--yarn-muted)]" data-testid={`player-score-${p.username}`}>{p.score} woven</span>
                <Status room={room} p={p} />
              </p>
            </div>
            {isHost && p.id !== me && <HostMenu p={p} send={send} />}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function RestartVote({ room, me, send }) {
  const mine = room.players.find((p) => p.id === me)?.restart;
  return (
    <div className="patch p-5 space-y-3" data-testid="restart-vote-box">
      <p className="text-sm text-[var(--yarn-muted)]">Want a fresh story? If everyone votes, it starts over.</p>
      <button className={`${mine ? "btn-yarn" : "btn-soft"} w-full h-12 inline-flex items-center justify-center gap-2`} onClick={() => send("restart_vote")} data-testid="restart-vote-button">
        <RotateCcw size={16} /> {mine ? "Cancel my restart vote" : "Vote to restart"} ({room.restart.count}/{room.restart.needed})
      </button>
    </div>
  );
}
